import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryFunction,
} from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import {
  ResourceCategory,
  ResourceOperationResponse,
  CreateResourceCategoryRequest,
  UpdateResourceCategoryRequest,
} from './types'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

export const useResourceCategories = () => {
  const fetchWithAuth = useFetchWithAuth()

  const getCategories: QueryFunction<ResourceCategory[]> = async () => {
    const response = await fetchWithAuth(
      `${API_URL}/api/resources/categories`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    )

    return response
  }

  return useQuery({
    queryKey: ['resource-categories'] as const,
    queryFn: getCategories,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useResourceCategory = (categoryId: string) => {
  const fetchWithAuth = useFetchWithAuth()

  const getCategory: QueryFunction<ResourceOperationResponse> = async () => {
    const response = await fetchWithAuth(
      `${API_URL}/api/resources/categories/${categoryId}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    )

    return response
  }

  return useQuery({
    queryKey: ['resource-category', categoryId] as const,
    queryFn: getCategory,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!categoryId,
  })
}

export const useCreateResourceCategoryMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (payload: CreateResourceCategoryRequest) => {
      const response = await fetchWithAuth(
        `${API_URL}/api/resources/categories`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      )

      return response as ResourceOperationResponse
    },
    onSuccess: () => {
      // Invalidate and refetch categories list
      queryClient.invalidateQueries({ queryKey: ['resource-categories'] })
    },
  })

  return [
    async (variables: {
      variables: { payload: CreateResourceCategoryRequest }
    }) => {
      return mutation.mutateAsync(variables.variables.payload)
    },
    { loading: mutation.isPending, error: mutation.error },
  ] as const
}

export const useUpdateResourceCategoryMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (variables: {
      categoryId: string
      payload: UpdateResourceCategoryRequest
    }) => {
      const { categoryId, payload } = variables

      const response = await fetchWithAuth(
        `${API_URL}/api/resources/categories/${categoryId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      )

      return response as ResourceOperationResponse
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch categories list
      queryClient.invalidateQueries({ queryKey: ['resource-categories'] })
      // Invalidate specific category
      queryClient.invalidateQueries({
        queryKey: ['resource-category', variables.categoryId],
      })
    },
  })

  return [
    async (variables: {
      variables: { categoryId: string; payload: UpdateResourceCategoryRequest }
    }) => {
      return mutation.mutateAsync({
        categoryId: variables.variables.categoryId,
        payload: variables.variables.payload,
      })
    },
    { loading: mutation.isPending, error: mutation.error },
  ] as const
}

export const useDeleteResourceCategoryMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (categoryId: string) => {
      const response = await fetchWithAuth(
        `${API_URL}/api/resources/categories/${categoryId}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        }
      )

      return response as ResourceOperationResponse
    },
    onSuccess: () => {
      // Invalidate and refetch categories list
      queryClient.invalidateQueries({ queryKey: ['resource-categories'] })
      // Invalidate resource documents that might reference this category
      queryClient.invalidateQueries({ queryKey: ['resource-documents'] })
    },
  })

  return [
    async (variables: { variables: { categoryId: string } }) => {
      return mutation.mutateAsync(variables.variables.categoryId)
    },
    { loading: mutation.isPending, error: mutation.error },
  ] as const
}
